// ModalPairDevice.tsx
import React, { useEffect, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { toast, ToastContainer } from "react-toastify";
import {
  BASE_URL,
  PPE_getApiSync,
  getValueFromLocalStorageFunc,
} from "../../../services/functions";
import { DomainAdd } from "@mui/icons-material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";

interface ModalPairDeviceProps {
  show: boolean;
  setShow: (value: boolean) => void;
  onSuccess: () => void;
  userOptions: { id: number; name: string }[];
}

type DeviceItem = {
  id?: number | string;
  id_device: string;
  name?: string;
};

// ------ helper fetch dengan pola getDataList-mu ------
async function fetchDevicesList(): Promise<DeviceItem[]> {
  const token = getValueFromLocalStorageFunc("IOT_TOKEN") ?? "";
  const apiUrl = `${BASE_URL}/v1/devices/list?limit=1000&offset=0`;

  console.log(
    "🔐 Token used for API request List Devices:",
    token ? "Token exists" : "No token",
  );
  console.log("🌐 API URL:", apiUrl);

  if (!token || token.length < 10) {
    console.error("❌ Invalid or missing token");
    throw new Error("AUTH_MISSING");
  }

  const response = await PPE_getApiSync(
    apiUrl,
    null,
    "application/json",
    "GET",
    token,
  );

  console.log("📥 Raw API Response (devices):", response);
  console.log(
    "📥 Response type:",
    typeof response,
    "Is Array:",
    Array.isArray(response),
  );

  // handle error style backend-mu
  if (response && response.statusCode) {
    const sc = String(response.statusCode);
    if (sc === "401") throw new Error("AUTH_INVALID");
    if (sc === "403") throw new Error("FORBIDDEN");
    throw new Error(response.msg || "FAILED_FETCH");
  }

  // normalisasi kalau backend kadang bungkus di data/items
  let list: any[] = [];
  if (Array.isArray(response)) list = response;
  else if (Array.isArray(response?.data)) list = response.data;
  else if (Array.isArray(response?.items)) list = response.items;

  if (!Array.isArray(list)) {
    console.warn("⚠️ Unexpected response format:", response);
    throw new Error("BAD_FORMAT");
  }

  // hanya ambil yg punya id_device
  return list
    .filter((it) => !!it?.id_device)
    .map((it) => ({
      id: it?.id ?? it?.device_id ?? it?.id_device,
      id_device: it?.id_device,
      name: it?.name,
    })) as DeviceItem[];
}

const ModalPairDevice: React.FC<ModalPairDeviceProps> = ({
  show,
  setShow,
  onSuccess,
  userOptions,
}) => {
  const [pairIdDevice, setPairIdDevice] = useState("");
  const [pairName, setPairName] = useState("");
  const [expiredAt, setExpiredAt] = useState("");

  const [devices, setDevices] = useState<DeviceItem[]>([]);
  const [isLoadingDevices, setIsLoadingDevices] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const handleClose = () => {
    setShow(false);
    setPairIdDevice("");
    setPairName("");
    setExpiredAt("");
    setLoadError(null);
  };

  const handleSubmit = async () => {
    if (!pairIdDevice || !pairName || !expiredAt) {
      toast.error(
        "Please fill all fields: Device ID, Username, and Expired At",
        {
          position: "top-center",
        },
      );
      return;
    }

    const foundUser = userOptions.find(
      (u) => u.name.toLowerCase() === pairName.toLowerCase(),
    );
    if (!foundUser) {
      toast.error("Username not found in user list", {
        position: "top-center",
      });
      return;
    }

    const token = getValueFromLocalStorageFunc("IOT_TOKEN") ?? "";

    const formattedExpiredAt = `${expiredAt} 00:00:00`;

    const payload = {
      id_device: pairIdDevice,
      id_user: foundUser.id,
      expired_at: formattedExpiredAt,
    };

    console.log("[DEBUG] Payload to /v1/device/pair:", payload);

    try {
      const res = await PPE_getApiSync(
        `${BASE_URL}/v1/device/pair`,
        payload,
        "application/json",
        "PUT",
        token,
      );
      console.log("[DEBUG] Pair response:", res);

      if (res?.detail === "pairing success") {
        toast.success("Pairing success", { position: "top-center" });
        handleClose();
        onSuccess();
      } else if (res?.statusCode) {
        toast.error(res.msg || "Failed to pair device", {
          position: "top-center",
        });
      } else {
        toast.error("Failed to pair device", { position: "top-center" });
      }
    } catch (e: any) {
      console.error(e);
      toast.error(`Error: ${e?.message || "Unknown error"}`, {
        position: "top-center",
      });
    }
  };

  useEffect(() => {
    if (!show) return;
    let mounted = true;
    setIsLoadingDevices(true);
    setLoadError(null);

    fetchDevicesList()
      .then((list) => {
        if (!mounted) return;
        setDevices(list);
        console.log("✅ Devices loaded:", list.length);
      })
      .catch((e: Error) => {
        if (!mounted) return;
        console.error("❌ Load devices failed:", e.message);
        if (e.message === "AUTH_MISSING" || e.message === "AUTH_INVALID") {
          setLoadError("Auth error. Please login again.");
          // kalau mau redirect, lakukan di halaman list, jangan di modal
        } else if (e.message === "FORBIDDEN") {
          setLoadError("Access denied.");
        } else if (e.message === "BAD_FORMAT") {
          setLoadError("Unexpected response format.");
        } else {
          setLoadError("Failed to fetch devices.");
        }
      })
      .finally(() => mounted && setIsLoadingDevices(false));

    return () => {
      mounted = false;
    };
  }, [show]);

  return (
    <>
      <Modal show={show} centered size="sm" backdrop onHide={handleClose}>
        <Modal.Header className={`modalheader modal-header-global create`}>
          <Modal.Title className={`dashboard-modal-title`}>
            <>
              <DomainAdd
                style={{
                  position: "absolute",
                  right: 15,
                  top: 25,
                  zIndex: 0,
                  transform: "scale(1.5)",
                }}
                className={`login-dropdown-icon`}
              />
              <FontAwesomeIcon
                icon={faPlus}
                className={`login-dropdown-icon`}
                color={"darkgreen"}
                style={{
                  position: "absolute",
                  right: 0,
                  top: 10,
                  zIndex: 0,
                  transform: "scale(1.5)",
                }}
              />
            </>
            <span className="login-icon-modal-title">Pair Device</span>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div style={{ display: "flex" }} className="row">
            {/* DEVICE ID DROPDOWN (hanya id_device) */}
            <Form.Group className="mb-1 col-12" controlId="id-device">
              <Form.Label className="mb-1 dash-modal-form-label required">
                Device ID
              </Form.Label>
              <Form.Select
                className="form-select"
                value={pairIdDevice}
                onChange={(e) => setPairIdDevice(e.target.value)}
                aria-label="Select device id"
                disabled={isLoadingDevices || !!loadError}
              >
                <option value="">
                  {isLoadingDevices
                    ? "Loading devices..."
                    : loadError
                      ? "Failed to load devices"
                      : "-- Select Device ID --"}
                </option>

                {!isLoadingDevices &&
                  !loadError &&
                  devices.map((d) => (
                    <option key={d.id_device} value={d.id_device}>
                      {d.id_device}
                    </option>
                  ))}
              </Form.Select>

              {loadError && (
                <small className="text-danger d-block mt-1">{loadError}</small>
              )}
            </Form.Group>

            {/* USERNAME DROPDOWN */}
            <Form.Group className="mb-1 col-12" controlId="id-user">
              <Form.Label className="mb-1 dash-modal-form-label required">
                Username
              </Form.Label>
              <Form.Select
                className="form-select"
                value={pairName}
                onChange={(e) => setPairName(e.target.value)}
                aria-label="Select username"
              >
                <option value="">-- Select Username --</option>
                {userOptions.map((user) => (
                  <option key={user.id} value={user.name}>
                    {user.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            {/* EXPIRED AT */}
            <Form.Group className="mb-1 col-12" controlId="id-expired">
              <Form.Label className="mb-1 dash-modal-form-label required">
                Expired At
              </Form.Label>
              <Form.Control
                type="date"
                className="form-control"
                value={expiredAt}
                onChange={(e) => setExpiredAt(e.target.value)}
              />
              <small className="text-muted">Pilih tanggal</small>
            </Form.Group>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={isLoadingDevices}
          >
            Save
          </Button>
          <Button variant="secondary" onClick={handleClose}>
            Close
          </Button>
        </Modal.Footer>

        <ToastContainer draggable pauseOnHover />
      </Modal>
    </>
  );
};

export default ModalPairDevice;
